/* eslint-disable max-lines */
import { AnswersArticleFilterEditorPlatform } from '@wix/ambassador-cc-helpcenter-v1-related-document/types';
import { EditorType } from '@wix/ambassador-meta-site-search-web/types';
import { useHttpClient, useFedops, useExperiments } from '@wix/fe-essentials-standalone';
import DOMPurify from 'isomorphic-dompurify';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { pushUrl } from '../../components/InnerLink';
import {
  SearchQuestionBIParams,
  useSearchBarBI,
} from '../../components/SearchBar/bi';
import { SearchResults } from '../../components/SearchResults';
import { NoResultsFound } from '../../components/SearchResults/NoResultsFound';
import { SearchResultsV2 } from '../../components/SearchResultsV2';
import NoResultsFoundV2 from '../../components/SearchResultsV2/NoResultsFoundV2';
import {
  ANSWER_GENERATION_TIME_BUDGET,
  ELASTIC_SEARCH_LIMIT,
  EXPERIMENTS,
  FEDOPS,
  LOCALES,
} from '../../constants';
import { Context } from '../../context';
import useIsAnswerGenerationMediaEnabled from '../../hooks/useIsAnswerGenerationMediaEnabled';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';
import useIsHelpCenterSearchResultV2Enabled from '../../hooks/useIsHelpCenterSearchResultV2Enabled';
import { useScrollDelegation } from '../../hooks/useScrollDelegation';
import { useWordByWordAnimation } from '../../hooks/useWordByWordAnimation';
import { getPageSeo } from '../../services/answers';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  GetWixServerSidePropsFunction,
  PageType,
  HttpStatusCode,
} from '../../types';
import { getCategoriesPaths } from '../../utils/categories';
import {
  streamGenerateAnswer,
  fetchRefinementQuestions,
} from '../../utils/generate-answer';
import { createLogger, LoggerNamespaces } from '../../utils/logger';
import { getKASearchResults } from '../../utils/pages/search/get-search-results';
import { metaSiteSearchEditorTypeConverter } from '../../utils/site-picker';
import { withServerSideProps } from '../../utils/with-server-side-props';
import { useStore } from '../../zustand/stores';

export const SEARCH_RESULTS_PAGE_SIZE = 12;

export type SearchPageProps = {
  searchResults: ArticleSearchResult;
  term: string;
  initCurrentPage: number;
  isContactSupport: boolean;
  biInputType?: string;
  editor?: AnswersArticleFilterEditorPlatform;
  previousPageType: PageType;
};

const itemIdKey = (term: string, page: number) => `${term}_${page}`;

const SearchPage: NextPage<SearchPageProps> = ({
  searchResults,
  term,
  initCurrentPage,
  isContactSupport,
  biInputType,
  editor,
  previousPageType,
}: SearchPageProps) => {
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const { pageTemplateData, setPageTemplateData } = useContext(Context);
  const httpClient = useHttpClient();
  const fedopsApi = useFedops();
  const [currentPage, setCurrentPage] = useState(initCurrentPage);
  const [searchTerm, setSearchTerm] = useState(term);
  const [isContactSupportState, setIsContactSupportState] = useState(isContactSupport);
  const { searchQuestionBI } = useSearchBarBI();
  const {
    locale,
    query: { editorType },
  } = useRouter();
  const [currentSearchResults, setCurrentSearchResults] =
    useState<ArticleSearchResult>(searchResults);
  const [isLoading, setIsLoading] = useState(false);
  const [usedArticlesInfo, setUsedArticlesInfo] = useState<{
    [id: string]: string;
  }>({});
  const [isProhibited, setIsProhibited] = useState<boolean | null>(null);
  const [refinementQuestions, setRefinementQuestions] = useState<
    string[]
  >([]);
  const [mediaRefsMap, setMediaRefsMap] = useState<Record<string, string>>({});
  const isFedopsInteractionEndedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentStreamIdRef = useRef(0);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const {
    displayedText: generatedAnswer,
    isAnimating,
    appendText,
    setText,
    finishStreaming,
    reset: resetAnimation,
  } = useWordByWordAnimation();
  const context = useContext(Context);
  const { searchSessionId, setSearchSessionId } = useStore((state) => state.biSlice);
  const isGenAnswerEnabled = useIsGenAnswer();
  const isAnswerGenerationMediaEnabled = useIsAnswerGenerationMediaEnabled();
  const isHelpCenterSearchResultV2Enabled =
    useIsHelpCenterSearchResultV2Enabled();
  const { experiments } = useExperiments({ suspense: false, readOnly: true });
  const isRefinementQuestionsEnabled = experiments.enabled(EXPERIMENTS.SPECS.ANSWER_GENERATION_REFINEMENT_QUESTIONS);

  useScrollDelegation(searchContainerRef, isHelpCenterSearchResultV2Enabled);
  const categoryIds =
    (currentSearchResults.items &&
      currentSearchResults?.items.map(
        (searchResult) => searchResult.categoryId || ''
      )) ||
    [];

  const initNumberOfResults = currentSearchResults.items
    ? currentSearchResults.items.length
    : 0;
  const allPaths = getCategoriesPaths(categoriesTree, categoryIds);
  const shouldGenerateAnswer =
    isGenAnswerEnabled && currentPage === 1 && !isContactSupport;

  useEffect(() => {
    if (searchResults) {
      setCurrentPage(initCurrentPage);
      setCurrentSearchResults(searchResults);
    }
  }, [searchResults, initCurrentPage, term]);

  useEffect(() => {
    setSearchTerm(term);
  }, [term]);

  const fetchSearchResults = useCallback(
    async (page: number, newTerm?: string) => {
      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: newTerm || searchTerm,
            pageSize: SEARCH_RESULTS_PAGE_SIZE,
            page,
            editor,
          },
        }
      );
      return data;
    },
    [httpClient, locale, searchTerm, editor]
  );

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    setPageTemplateData({ ...pageTemplateData, itemId: itemIdKey(term, page) });
    setCurrentSearchResults(await fetchSearchResults(page));
    await pushUrl(`${locale}/search?term=${term}&page=${page}`, true);
  };

  const sendSearchQuestionBI = useCallback(async (
    generatedAnswerText?: string | null,
    usedArticles?: string[],
    isProhibitedTerm?: boolean
  ) => {
    const searchQuestionBIParams: SearchQuestionBIParams = {
      results: searchResults?.items as ArticleSearchResultItem[],
      pageType: previousPageType,
      inputText: term,
      searchMethod: searchResults?.searchMethod,
      searchVersion: searchResults?.searchVersion,
      inputType: biInputType,
      isContactSupport,
      isProhibited: isProhibitedTerm,
      isGenerateAnswerFailed:
        generatedAnswerText?.length === 0 && shouldGenerateAnswer,
      searchSessionId,
    };

    const selectedSite = context.siteSelectorData?.selectedSite || undefined;
    await searchQuestionBI(
      searchQuestionBIParams,
      generatedAnswerText,
      usedArticles,
      selectedSite
    );
  }, [
    searchResults,
    previousPageType,
    term,
    biInputType,
    isContactSupport,
    shouldGenerateAnswer,
    searchSessionId,
    context.siteSelectorData?.selectedSite,
    searchQuestionBI
  ]);

  const generateAnswer = useCallback(async (newTerm: string, isForceGenerateAnswer?: boolean):
    Promise<{ completed: boolean; aborted: boolean }> => {
    if (searchResults?.items?.length > 0 && shouldGenerateAnswer || isForceGenerateAnswer) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      resetAnimation();
      setUsedArticlesInfo({});
      setIsProhibited(null);
      setMediaRefsMap({});
      isFedopsInteractionEndedRef.current = false;

      currentStreamIdRef.current += 1;
      const thisStreamId = currentStreamIdRef.current;

      abortControllerRef.current = new AbortController();
      try {
        fedopsApi.interactionStarted(
          FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION_STREAMED
        );

        if (isRefinementQuestionsEnabled) {
          fetchRefinementQuestions(newTerm, abortControllerRef.current)
            .then((questions) => setRefinementQuestions(questions ?? []))
            .catch(() => setRefinementQuestions([]));
        }
        let agPromptId;
        if (isAnswerGenerationMediaEnabled) {
          agPromptId = '59524de8-c751-4ca6-9de1-1b56e43ab509';
        } else if (isHelpCenterSearchResultV2Enabled) {
          agPromptId = '9038695e-124a-4735-a816-59e3dff93ed5';
        }

        await streamGenerateAnswer(
          {
            searchTerm: newTerm || searchTerm,
            language: locale ?? LOCALES.EN,
            editorType,
            agPromptId,
          },
          ANSWER_GENERATION_TIME_BUDGET,
          (partial: any) => {
            if (thisStreamId !== currentStreamIdRef.current) {
              return;
            }

            if (
              typeof partial.generatedAnswerChunk === 'string' &&
              partial.generatedAnswerChunk.length > 0
            ) {
              if (!isFedopsInteractionEndedRef.current) {
                fedopsApi.interactionEnded(
                  FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION_STREAMED,
                  {
                    customParams: {
                      source: FEDOPS.ANSWER_GENERATION_SOURCES.STREAMING,
                    },
                  }
                );
                isFedopsInteractionEndedRef.current = true;
              }
              appendText(partial.generatedAnswerChunk);
              setIsProhibited(false);
            } else if (partial.generatedAnswer !== null) {
              if (!isFedopsInteractionEndedRef.current) {
                fedopsApi.interactionEnded(
                  FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION_STREAMED,
                  {
                    customParams: {
                      source: FEDOPS.ANSWER_GENERATION_SOURCES.CACHE,
                    },
                  }
                );
                isFedopsInteractionEndedRef.current = true;
              }
              const answerText = partial.generatedAnswer.answer
                ? JSON.parse(partial.generatedAnswer.answer).solution
                : '';

              finishStreaming();
              setText(answerText);
              setIsProhibited(partial.generatedAnswer.prohibited);
              setUsedArticlesInfo(
                partial.generatedAnswer.metadata.usedArticlesInfo
              );
              setMediaRefsMap(
                partial.generatedAnswer.metadata.mediaRefsMap || {}
              );
              void sendSearchQuestionBI(
                answerText,
                Object.keys(partial.generatedAnswer.metadata.usedArticlesInfo),
                partial.generatedAnswer.prohibited
              );
            }
          },
          abortControllerRef.current
        );
        finishStreaming();
        return { completed: true, aborted: false };
      } catch (error) {
        const isAborted = error instanceof Error && error.name === 'AbortError';
        resetAnimation(true);
        setUsedArticlesInfo({});
        setMediaRefsMap({});
        await sendSearchQuestionBI('');
        return { completed: false, aborted: isAborted };
      }
    }
    return { completed: false, aborted: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fedopsApi, httpClient, isContactSupport, sendSearchQuestionBI, term, searchTerm]);

  useEffect(() => {
    void (async () => {
      setIsLoading(true);
      const { aborted } = await generateAnswer(term);
      if (!aborted) {
        setIsLoading(false);
      }
    })();
  }, [term]);

  useEffect(() => {
    if (!shouldGenerateAnswer) {
      void sendSearchQuestionBI();
    }
  }, [shouldGenerateAnswer, term]);

  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  const onSearch = async (newTerm: string) => {
    abortControllerRef.current?.abort();
    setIsLoading(true);
    setIsContactSupportState(false);
    setSearchSessionId(uuidv4());
    setRefinementQuestions([]);

    try {
      resetAnimation(false);
      setSearchTerm(newTerm);
      await pushUrl(
        `${locale}/search?term=${newTerm}&editorType=${editorType}&previousPageType=${previousPageType}`
        , true);
      setCurrentSearchResults({
        ...currentSearchResults,
        isLoading: true,
      });
      const newSearchResults = await fetchSearchResults(1, newTerm);
      setIsContactSupportState(newSearchResults.isContactSupport || false);
      setCurrentSearchResults({ ...newSearchResults, isLoading: false });
      if (newSearchResults.isContactSupport) {
        setIsLoading(false);
        return;
      }
      if (newSearchResults.items.length > 0) {
        const { aborted } = await generateAnswer(newTerm, true);
        if (!aborted) {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('error', error);
      setIsLoading(false);
    }
  };

  const retryGenerateAnswer = async () => {
    setIsLoading(true);
    setSearchSessionId(uuidv4());
    const { aborted } = await generateAnswer(searchTerm);
    if (!aborted) {
      setIsLoading(false);
    }
  };

  if (
    term.length > 0 &&
    currentSearchResults &&
    initNumberOfResults &&
    initNumberOfResults > 0 || isLoading
  ) {
    const limitedSearchResults = {
      ...currentSearchResults,
      itemsCount: Math.min(
        currentSearchResults.itemsCount,
        ELASTIC_SEARCH_LIMIT
      ),
    };

    return (
      <div ref={searchContainerRef}>
        {isHelpCenterSearchResultV2Enabled ? (
          <SearchResultsV2
            isLoading={isLoading}
            searchTerm={searchTerm}
            searchResults={limitedSearchResults}
            isContactSupport={isContactSupportState}
            categoriesPaths={allPaths}
            locale={locale as string}
            generatedAnswer={generatedAnswer}
            isProhibited={isProhibited}
            isStreaming={isAnimating}
            usedArticlesInfo={usedArticlesInfo}
            mediaRefsMap={mediaRefsMap}
            retryGenerateAnswer={retryGenerateAnswer}
            onSearch={onSearch}
            refinementQuestions={refinementQuestions}
          />
        ) : (
          <SearchResults
            term={term}
            searchResults={limitedSearchResults}
            categoriesPaths={allPaths}
            currentPage={currentPage}
            onPageChange={onPageChange}
            isLoading={isLoading}
            generatedAnswer={generatedAnswer}
            usedArticlesInfo={usedArticlesInfo}
            isProhibited={isProhibited}
            showContactSupport={isContactSupport}
            retryGenerateAnswer={retryGenerateAnswer}
            isStreaming={isAnimating}
          />
        )}
      </div>
    );
  }

  return isHelpCenterSearchResultV2Enabled ? (
    <NoResultsFoundV2
      initialSearchTerm={searchTerm}
      onSearch={onSearch}
    />
  ) : (
    <NoResultsFound term={term} />
  );
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ req, res, locale, query }) => {
    const logger = createLogger(LoggerNamespaces.Server);
    const {
      term,
      page = 1,
      previousPageType,
      inputType = '',
      editorType,
    } = query;
    const editor = editorType
      ? metaSiteSearchEditorTypeConverter(editorType as EditorType)
      : null;
    let sanitizedTerm = '',
      pageNumber = 1;
    try {
      sanitizedTerm = DOMPurify.sanitize(decodeURIComponent(term as string), {
        ALLOWED_TAGS: [],
      });
      pageNumber = !isNaN(Number(page)) ? Number(page) : 1;
    } catch (error: unknown) {
      logger
        .withAspects(req.aspects)
        .error('Error decoding term or page', term, page, error);
      const message = error instanceof Error ? error.message : String(error);
      res.status(HttpStatusCode.BAD_REQUEST).send(message);
    }
    const { searchResults = [], isContactSupport = false } =
      await getKASearchResults(
        req.aspects,
        res,
        sanitizedTerm,
        pageNumber,
        locale,
        editor
      );

    const pageSeo = await getPageSeo(
      req.aspects,
      locale,
      PageType.SearchResults,
      res.locals.redisClient
    );

    return {
      props: {
        term: sanitizedTerm,
        pageType: PageType.SearchResults,
        pageData: { searchResults, term: sanitizedTerm },
        searchResults: { ...searchResults, isLoading: false },
        initCurrentPage: pageNumber,
        itemId: itemIdKey(sanitizedTerm, pageNumber),
        pageSeo,
        isContactSupport,
        biInputType: inputType,
        editor,
        previousPageType: !isNaN(Number(previousPageType))
          ? Number(previousPageType)
          : null,
      },
    };
  });

export default SearchPage;
